import { useEffect, useLayoutEffect, useRef, useState } from "react"

import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url"

import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import { getPreviewPdfBlob } from "@/lib/api"
import type { PreviewDoc } from "@/providers/PreviewProvider"

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

/** Native in-app PDF preview — renders the Graph-converted document with our
 *  own paging UI instead of embedding Office Online's hosted iframe. */
export function PdfViewer({ doc, onUnavailable }: { doc: PreviewDoc; onUnavailable: () => void }) {
  const { t } = useI18n()
  const [fileUrl, setFileUrl] = useState<string | null>(null)
  const [numPages, setNumPages] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)
  const [width, setWidth] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false

    getPreviewPdfBlob({ sourceUrl: doc.url, driveId: doc.driveId, itemId: doc.itemId })
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setFileUrl(objectUrl)
      })
      .catch(() => {
        if (!cancelled) onUnavailable()
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.url, doc.driveId, doc.itemId])

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(entry.contentRect.width)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="flex h-full flex-col">
      <div ref={containerRef} className="flex-1 overflow-y-auto bg-muted/20">
        {fileUrl ? (
          <Document
            file={fileUrl}
            loading={<PdfLoading />}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            onLoadError={onUnavailable}
            className="flex justify-center py-4"
          >
            {width > 0 && <Page pageNumber={pageNumber} width={width - 32} />}
          </Document>
        ) : (
          <PdfLoading />
        )}
      </div>

      {numPages > 1 && (
        <div className="flex shrink-0 items-center justify-center gap-3 border-t bg-muted/40 py-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
            aria-label={t("preview.prevPage")}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="text-xs text-muted-foreground">
            {t("preview.pageOf", { page: pageNumber, total: numPages })}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            disabled={pageNumber >= numPages}
            onClick={() => setPageNumber((p) => Math.min(numPages, p + 1))}
            aria-label={t("preview.nextPage")}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

function PdfLoading() {
  const { t } = useI18n()
  return (
    <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="size-4 animate-spin" />
      {t("preview.loading")}
    </div>
  )
}
